// client/src/components/dashboard/Dashboard.js
import './Dashboard.css'
import { useMemo, useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toastr from 'toastr'
import useAuth from '../../hooks/useAuth'
import useDashboard from '../../hooks/useDashboard'
import { formatMemberSince, formatDateTime } from '../../utils/format'
import { pluralize } from '../../utils/pluralize'
import { services } from '../../services'
import PageHeader from '../common/layout/PageHeader'
import NextMatchHeroCard from './NextMatchHeroCard'
import RecentAwards from './RecentAwards'
import CancelMatchModal from '../common/CancelMatchModal'
import StaleDataBanner from '../common/StaleDataBanner'
import { APP_LOGO_SRC } from '../../config/branding'

function Dashboard() {
  const navigate = useNavigate()
  const { isAdmin, user } = useAuth()

  // Use the new unified dashboard hook
  const {
    nextMatch,
    recentAwards,
    overdueMatchesCount,
    leagueCount,
    matchesPlayedCount,
    loading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useDashboard()

  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [matchToCancel, setMatchToCancel] = useState(null)
  const [matchDetailsForCancel, setMatchDetailsForCancel] = useState(null)
  const [isCanceling, setIsCanceling] = useState(false)
  const [firstVisit, setFirstVisit] = useState(false)
  const [staleDataWarning, setStaleDataWarning] = useState(false)

  // Handle first visit tracking
  useEffect(() => {
    if (!loading && user) {
      const visitedKey = `dashboardVisited_${user.id}`
      const hasVisited = localStorage.getItem(visitedKey)
      if (!hasVisited) {
        setFirstVisit(true)
        localStorage.setItem(visitedKey, 'true')
      }
    }
  }, [loading, user])

  // Derive user display name - only use firstname if it exists, otherwise null
  const name = user?.firstname?.trim() ? user.firstname : null
  const memberSince = user?.created_at || null

  const badgeCount = overdueMatchesCount

  const formattedMemberSince = useMemo(
    () => formatMemberSince(memberSince),
    [memberSince],
  )

  const handleCancelMatchClick = async (match) => {
    setMatchToCancel(match)

    // Fetch full match details including phone numbers before opening modal
    try {
      const result = await services.match.getMatch(match.id)
      if (result.success && result.data) {
        setMatchDetailsForCancel(result.data)
      }
    } catch (error) {
      console.error('Failed to load match details for cancel modal:', error)
      // Continue with basic info even if detailed fetch fails
    }

    // Open modal after fetch attempt
    setCancelModalOpen(true)
  }

  const handleConfirmCancel = async () => {
    if (!matchToCancel) return

    setIsCanceling(true)
    try {
      const result = await services.match.cancelMatch(matchToCancel.id)
      if (result.success) {
        toastr.success('Match canceled successfully')
        setCancelModalOpen(false)
        setMatchToCancel(null)
        setStaleDataWarning(false) // Clear any previous stale data warning
        // Reload dashboard data
        try {
          await refetchDashboard()
        } catch (reloadError) {
          console.error(
            'Failed to reload dashboard after cancellation:',
            reloadError,
          )
          setStaleDataWarning(true) // Show inline banner instead of toast
        }
      } else {
        toastr.error(result.error || 'Failed to cancel match')
      }
    } catch (error) {
      console.error('Failed to cancel match:', error)
      toastr.error('Failed to cancel match. Please try again.')
    } finally {
      setIsCanceling(false)
    }
  }

  const handleCloseModal = () => {
    if (!isCanceling) {
      setCancelModalOpen(false)
      setMatchToCancel(null)
      setMatchDetailsForCancel(null)
    }
  }

  const handleRetryRefresh = async () => {
    setStaleDataWarning(false)
    try {
      await refetchDashboard()
    } catch (error) {
      console.error('Retry refresh failed:', error)
      toastr.error(
        'Failed to refresh data. Please try again or reload the page.',
      )
      setStaleDataWarning(true)
    }
  }

  const getOpponentName = (match) => {
    if (!match) return 'Opponent'
    try {
      return (
        services.match.getOpponentName(match, match.current_player_id) ||
        'Opponent'
      )
    } catch {
      return 'Opponent'
    }
  }

  const formatMatchDateTime = (datetime) => {
    if (!datetime) return 'TBD'
    try {
      return formatDateTime(datetime)
    } catch {
      return String(datetime)
    }
  }

  if (loading) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            color: 'white',
            fontSize: '1.2rem',
          }}
        >
          Loading your dashboard...
        </div>
      </div>
    )
  }

  return (
    <div className="dashboard-container">
      <CancelMatchModal
        isOpen={cancelModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmCancel}
        isProcessing={isCanceling}
        matchDetails={
          matchToCancel
            ? {
                opponentName: getOpponentName(matchToCancel),
                dateTime: formatMatchDateTime(matchToCancel.match_datetime),
                matchDatetime: matchToCancel.match_datetime,
                opponentPhone: services.match.getOpponentPhone(
                  matchToCancel,
                  matchDetailsForCancel,
                ),
              }
            : {}
        }
      />

      <div
        style={{
          position: 'absolute',
          top: '1rem',
          left: '1rem',
          right: '1rem',
          zIndex: 10,
        }}
      >
        <PageHeader
          showHome={false}
          showBack={false}
          showProfile={true}
          showLogout={true}
        />
      </div>

      {/* Stale Data Warning Banner */}
      <StaleDataBanner
        show={staleDataWarning}
        onRetry={handleRetryRefresh}
        onDismiss={() => setStaleDataWarning(false)}
        className="stale-data-banner--dashboard"
      />

      <div className="dashboard-hero">
        <img
          src={APP_LOGO_SRC}
          alt="Serve Sync Tennis logo"
          className="dashboard-logo"
        />
        <h1 className="dashboard-title">
          {firstVisit
            ? name
              ? `Welcome, ${name}!`
              : 'Welcome!'
            : name
              ? `Welcome back, ${name}!`
              : 'Welcome back!'}
        </h1>
      </div>

      {/* Next Match Hero Card - Above the fold */}
      <div className="next-match-hero-wrapper">
        <NextMatchHeroCard
          match={nextMatch}
          loading={loading}
          error={dashboardError}
          onScheduleMatch={() => navigate('/my-leagues')}
          onCancelMatch={handleCancelMatchClick}
        />
      </div>

      <div className="dashboard-grid">
        {/* Stats & Info Card */}
        <div className="dashboard-card dashboard-side-card">
          <h3 className="dashboard-section-title">Profile Snapshot</h3>

          {/* Member Since - displayed prominently above stats */}
          {formattedMemberSince && (
            <div className="member-since-badge">
              <span className="member-since-label">Member Since:</span>
              <span className="member-since-date">{formattedMemberSince}</span>
            </div>
          )}

          <div className="quick-stats">
            <div className="stat-card">
              <div className="stat-number">{leagueCount || 0}</div>
              <div className="stat-label">
                {pluralize(leagueCount, 'Active League')}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{matchesPlayedCount || 0}</div>
              <div className="stat-label">Matches Played</div>
            </div>
          </div>

          {/* View My History Link */}
          {user?.primary_player_id && (
            <div className="view-history-link-container">
              <Link
                to={`/players/${user.primary_player_id}/history`}
                className="view-history-link"
                data-track="navigation.player_history"
              >
                📊 View My History
              </Link>
            </div>
          )}

          <h3 className="dashboard-section-title">Recent Awards</h3>
          <RecentAwards
            awards={recentAwards}
            loading={loading}
            error={dashboardError}
            onRetry={refetchDashboard}
          />
        </div>

        {/* Main Actions & Features */}
        <div className="dashboard-card dashboard-main">
          <h3 className="dashboard-section-title">Action Center</h3>
          <div className="dashboard-button-group">
            <button
              className="dashboard-button register-button"
              onClick={() => navigate('/register')}
              data-track="navigation.register"
            >
              <span>🏆</span>
              <div>
                <div>Register for League</div>
                <small>Join a new competition</small>
              </div>
            </button>

            <button
              className="dashboard-button myleagues-button"
              onClick={() => navigate('/my-leagues')}
              data-track="navigation.my_leagues"
            >
              <span>🎾</span>
              <div>
                <div>My Leagues</div>
                <small>View your active leagues</small>
              </div>
            </button>

            <button
              className="dashboard-button scheduled-matches-button"
              onClick={() => navigate('/scheduled-matches')}
              data-track="navigation.scheduled_matches"
            >
              <span>📅</span>
              <div>
                <div>My Scheduled Matches</div>
                <small>View upcoming matches</small>
              </div>
              {badgeCount > 0 && (
                <span
                  className="match-count-badge"
                  aria-label={`${badgeCount} overdue ${badgeCount === 1 ? 'match' : 'matches'}`}
                >
                  {badgeCount}
                </span>
              )}
            </button>

            {isAdmin && (
              <button
                className="dashboard-button admin-button"
                onClick={() => navigate('/admin/dashboard')}
                data-track="navigation.admin_dashboard"
              >
                <span>⚙️</span>
                <div>
                  <div>Admin Dashboard</div>
                  <small>Manage the platform</small>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
